import { useRef, useState, type MutableRefObject } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../hooks/useAuth';
import { useUI } from '../../providers/MoleUIContext';
import { allowOrWarn } from '../../util/Util';
import type { VoucherRow } from './voucherTypes';
import { canDrop } from '../../util/access';

export const useVoucherDelete = (
  onSuccess: () => void,
  toastIdRef?: MutableRefObject<string | number>,
) => {
  const { api, access } = useAuth();
  const { setLoadingMessage, queueReRun, t } = useUI();
  const [toDelete, setToDelete] = useState<VoucherRow | null>(null);
  const [toDeleteBulk, setToDeleteBulk] = useState<VoucherRow[]>([]);
  const tableRef = useRef<any>(null);
  const internalToastIdRef = useRef<string | number>(-1);
  const toastRef = toastIdRef ?? internalToastIdRef;

  const canDelete = canDrop(access, 'voucher');
  const canDeleteBulk = canDrop(access, 'voucher', 'delete_bulk');

  const handleDelete = async () => {
    if (!toDelete) return;
    setLoadingMessage('DELETE');
    const res = await api.delete(`/vouchers/${toDelete.id}`);
    if (res?.status === 204) {
      toastRef.current = toast.loading(
        `${t('common:toast.success.delete')} ${t('common:toast.loading.refresh')}`,
      );
      onSuccess();
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(handleDelete);
    }
    setLoadingMessage(null);
    setToDelete(null);
  };

  const handleBulkDelete = async () => {
    if (toDeleteBulk.length === 0) return;
    setLoadingMessage('DELETE');
    const res = await api.post('/vouchers/delete', {
      ids: toDeleteBulk.map((row) => row.id),
    });
    if (res?.status === 204) {
      toastRef.current = toast.loading(
        `${t('common:toast.success.delete')} ${t('common:toast.loading.refresh')}`,
      );
      tableRef.current?.resetRowSelection();
      onSuccess();
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(handleBulkDelete);
    }
    setLoadingMessage(null);
    setToDeleteBulk([]);
  };

  const requestRowDelete = (row: VoucherRow) => {
    if (allowOrWarn(canDrop(access, 'voucher'))) {
      setToDelete(row);
    }
  };

  const requestSelectionDelete = (table: any) => {
    if (!allowOrWarn(canDrop(access, 'voucher', 'delete_bulk'))) return;
    const rows = table.getSelectedRowModel().flatRows.map((entry: any) => entry.original);
    if (rows.length === 1) {
      setToDelete(rows[0]);
    } else if (rows.length > 1) {
      tableRef.current = table;
      setToDeleteBulk(rows);
    }
  };

  return {
    canDelete,
    canDeleteBulk,
    toDelete,
    toDeleteBulk,
    setToDelete,
    setToDeleteBulk,
    handleDelete,
    handleBulkDelete,
    requestRowDelete,
    requestSelectionDelete,
    isConfirmOpen: Boolean(toDelete) || toDeleteBulk.length > 0,
  };
};
